// EDIFACT Glossar - Übersichtsseite
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { LuSearch, LuTriangleAlert, LuSearchX } from "react-icons/lu";
import SeoHead from "../../components/SeoHead";
import { pageMeta } from "../../data/pageMeta";
import TopBanner from "../../components/TopBanner";
import { glossaryTerms, categories, getAllTermsAlphabetically, searchTerms } from "../../data/glossaryTerms";

export default function GlossarIndex() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  // Sanitize search input (XSS-Prevention)
  const sanitizeInput = (input) => {
    return input.replace(/[<>'"&]/g, '');
  };

  // Filter Begriffe
  const getFilteredTerms = () => {
    const safeQuery = sanitizeInput(searchQuery);
    let terms = safeQuery 
      ? searchTerms(safeQuery)
      : getAllTermsAlphabetically();

    // Validate category against allowed values
    const allowedCategories = ["all", "segment", "nachrichtentyp", "qualifier", "standard", "konzept"];
    const safeCategory = allowedCategories.includes(selectedCategory.toLowerCase()) ? selectedCategory : "all";

    if (safeCategory !== "all") {
      terms = terms.filter(t => t.category.toLowerCase() === safeCategory);
    }

    return terms;
  };

  const filteredTerms = getFilteredTerms();

  // Gruppiere nach Anfangsbuchstaben
  const groupedTerms = filteredTerms.reduce((acc, term) => {
    const firstLetter = term.term[0].toUpperCase();
    if (!acc[firstLetter]) acc[firstLetter] = [];
    acc[firstLetter].push(term);
    return acc;
  }, {});

  // Schema.org Structured Data für Glossar
  // Nur statische Daten aus glossaryTerms.js - kein User-Input!
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    "name": "EDIFACT Glossar",
    "description": "Komplettes EDIFACT-Glossar mit allen wichtigen Segmenten, Nachrichtentypen und Begriffen",
    "url": "https://edi-converter.de/glossar",
    "inLanguage": "de",
    "publisher": {
      "@type": "Organization",
      "name": "EDI-Converter.de",
      "url": "https://edi-converter.de"
    },
    // Nur Pre-Defined Terms - kein User-Generated Content
    "hasDefinedTerm": glossaryTerms.map(term => ({
      "@type": "DefinedTerm",
      "name": String(term.term).substring(0, 100), // Length limit
      "description": String(term.description).substring(0, 500), // Length limit
      "url": `https://edi-converter.de/glossar/${encodeURIComponent(term.slug)}`,
      "termCode": String(term.term).substring(0, 50),
      "inDefinedTermSet": "https://edi-converter.de/glossar"
    }))
  };

  return (
    <>
      <SeoHead
        {...pageMeta["/glossar"]}
        keywords="EDIFACT Glossar, EDI Begriffe, EDIFACT Segmente, UNB, UNH, INVOIC, ORDERS, DESADV, EDI Lexikon"
        ogUrl="https://edi-converter.de/glossar"
      />

      {/* Schema.org Structured Data - Safe: nur statische Daten */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(schemaData)
      }} />

      <TopBanner />

      <section className="page-hero page-hero-center">
        <div className="container">
          <h1 className="h1">EDIFACT Glossar</h1>
          <p className="lead">
            Alle wichtigen EDIFACT-Begriffe, Segmente und Nachrichtentypen verständlich erklärt – mit Beispielen und Tool-Links.
          </p>

          {/* Suchfeld */}
          <div className="input-icon-wrap" style={{ maxWidth: 560, margin: "28px auto 0" }}>
            <LuSearch size={18} aria-hidden="true" />
            <input
              type="search"
              className="input"
              placeholder="Begriff suchen (z.B. UNB, INVOIC, Rechnung)..."
              aria-label="Glossar durchsuchen"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Kategorie-Filter */}
          <div className="row row-center" style={{ marginTop: 20, gap: 8 }}>
            <button
              type="button"
              className="chip"
              aria-pressed={selectedCategory === "all"}
              onClick={() => setSelectedCategory("all")}
            >
              Alle <span className="chip-count">{glossaryTerms.length}</span>
            </button>
            {categories.map(cat => (
              <button
                type="button"
                key={cat.id}
                className="chip"
                aria-pressed={selectedCategory === cat.id}
                onClick={() => setSelectedCategory(cat.id)}
              >
                {cat.name}{" "}
                <span className="chip-count">
                  {glossaryTerms.filter(t => t.category.toLowerCase() === cat.id).length}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="section-sm">
        <div className="container">
          {/* A-Z Sprungmarken Navigation (SEO-Booster) */}
          {filteredTerms.length > 0 && (
            <nav aria-label="Schnellnavigation A–Z" style={{ marginBottom: 40 }}>
              <div className="small strong" style={{ marginBottom: 10 }}>Schnellnavigation:</div>
              <div className="letter-nav">
                {Object.keys(groupedTerms).sort().map(letter => (
                  <a key={letter} href={`#letter-${letter}`}>{letter}</a>
                ))}
              </div>
            </nav>
          )}

          {/* Begriffe gruppiert nach Buchstaben */}
          {Object.keys(groupedTerms).sort().map(letter => (
            <div key={letter} id={`letter-${letter}`} className="letter-group">
              <h2 className="letter-heading">{letter}</h2>
              <div className="grid grid-3">
                {groupedTerms[letter].map(term => (
                  <Link key={term.slug} to={`/glossar/${term.slug}`} className="card card-link term-card">
                    <div className="row" style={{ gap: 8 }}>
                      <span className="badge badge-mono">{term.term}</span>
                      <span className="xsmall muted">{term.category}</span>
                    </div>
                    <div className="term-card-name">{term.fullName}</div>
                    <div className="term-card-desc">{term.description}</div>
                  </Link>
                ))}
              </div>
            </div>
          ))}

          {filteredTerms.length === 0 && (
            <div className="card card-pad-lg text-center" style={{ padding: "56px 24px" }}>
              <span className="icon-tile icon-tile-lg"><LuSearchX size={24} aria-hidden="true" /></span>
              <h3 className="h3" style={{ marginTop: 16 }}>Keine Ergebnisse</h3>
              <p className="muted" style={{ marginTop: 6 }}>
                Für "{searchQuery}" wurden keine Begriffe gefunden.
              </p>
            </div>
          )}

          {/* Disclaimer */}
          <div className="alert alert-warning" style={{ marginTop: 64 }}>
            <LuTriangleAlert size={20} aria-hidden="true" />
            <div>
              <h3 className="alert-title" style={{ fontSize: "inherit" }}>Hinweis</h3>
              <p style={{ marginTop: 4 }}>
                Dieses Glossar ist ein frei zugängliches Lern- und Informationsangebot. Die Inhalte wurden automatisiert erstellt bzw. redaktionell aufbereitet. Alle Angaben erfolgen ohne Anspruch auf Vollständigkeit oder Fehlerfreiheit. Verbindlich sind ausschließlich die offiziellen UN/EDIFACT-Spezifikationen. Eine Haftung für Schäden aus der Nutzung wird ausgeschlossen.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
